import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Box, Typography, Card, CardContent, FormControl, InputLabel, Select, MenuItem, Button,
    CircularProgress, Alert, List, ListItem, ListItemText, Paper, Grid, Divider, Link as MuiLink,
    Accordion, AccordionSummary, AccordionDetails, Chip, OutlinedInput
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download'; // Corrected Import
import {
    ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ReferenceArea, ZAxis, ReferenceLine
} from 'recharts';
import html2canvas from 'html2canvas';
import api from './api';
import config from './config';

// Helper function to determine ACS Style Name
const getAcsStyleName = (proactivity, reactivity) => {
    if (proactivity === null || reactivity === null || proactivity === undefined || reactivity === undefined) return "Not Determined";
    const pro = Number(proactivity);
    const react = Number(reactivity);
    if (pro > 0 && react > 0) return "Expressive";
    if (pro < 0 && react < 0) return "Analytical";
    if (pro > 0 && react < 0) return "Driver";
    if (pro < 0 && react > 0) return "Amiable";
    if (pro === 0 && react > 0) return "Amiable Tendency";
    if (pro === 0 && react < 0) return "Analytical Tendency";
    if (react === 0 && pro > 0) return "Driver Tendency";
    if (react === 0 && pro < 0) return "Analytical Tendency";
    if (pro === 0 && react === 0) return "Balanced/Neutral";
    return "Not Determined";
};

const memberColors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00C49F', '#FFBB28', '#FF8042', '#0088FE', '#A52A2A', '#5F9EA0', '#D2691E', '#FF7F50'];
const lowScoreBg = "#e3f2fd";
const midScoreBg = "#f3e5f5";
const highScoreBg = "#e0f2f1";

// Custom shape for the ACS chart points
const CustomCohortACSShape = (props) => {
    const { cx, cy, fill, payload, type } = props;
    if (cx == null || cy == null) return null;

    let radius;
    if (type === 'member') {
        const minRadius = 7;
        const maxRadius = 20;
        const scaleFactor = 5;
        radius = Math.min(maxRadius, Math.max(minRadius, (payload.z || 1) * scaleFactor));
    } else { // 'average'
        radius = 16;
    }

    let strokeColor = type === 'average' ? "#000" : "#444";
    let strokeWidth = type === 'average' ? 2.5 : 1.5;

    if (type === 'average') {
        const points = [];
        for (let i = 0; i < 5; i++) {
            points.push(`${cx + radius * Math.cos(Math.PI / 2 + (2 * Math.PI * i) / 5)},${cy - radius * Math.sin(Math.PI / 2 + (2 * Math.PI * i) / 5)}`);
            points.push(`${cx + (radius / 2) * Math.cos(Math.PI / 2 + (2 * Math.PI * (i + 0.5)) / 5)},${cy - (radius / 2) * Math.sin(Math.PI / 2 + (2 * Math.PI * (i + 0.5)) / 5)}`);
        }
        return <polygon points={points.join(' ')} fill={fill} stroke={strokeColor} strokeWidth={strokeWidth} />;
    }
    return <circle cx={cx} cy={cy} r={radius} fill={fill} stroke={strokeColor} strokeWidth={strokeWidth} opacity={0.75} />;
};

// Custom shape for the WEP chart points
const CustomCohortWEPShape = (props) => {
    const { cx, cy, fill, type, payload } = props;
    if (cx == null || cy == null) return null;
    let radius = 7;
    let stroke = "#555";
    let strokeWidth = 1;
    let actualFill = fill;

    if (payload && payload.isCurrentUserPoint) {
        actualFill = '#ff9800';
        stroke = '#e65100';
        strokeWidth = 2;
        radius = 9;
    }

    if (type === 'average') {
        return (
            <rect
                x={cx - (radius + 1)}
                y={cy - (radius + 1)}
                width={(radius + 1) * 2}
                height={(radius + 1) * 2}
                fill={actualFill}
                stroke="#000"
                strokeWidth={1.5}
                transform={`rotate(45 ${cx} ${cy})`}
            />
        );
    }
    return <circle cx={cx} cy={cy} r={radius * (payload && payload.isCurrentUserPoint ? 1 : 0.8)} fill={actualFill} stroke={stroke} strokeWidth={strokeWidth} opacity={0.7} />;
};

function AdminTools({ userId, userRole }) {
    // General State
    const [cohorts, setCohorts] = useState([]);
    const [surveyRounds, setSurveyRounds] = useState([]);
    const [error, setError] = useState('');
    const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);

    // SPI Summary State
    const [selectedCohortsForSpi, setSelectedCohortsForSpi] = useState([]);
    const [cohortSpiSummary, setCohortSpiSummary] = useState(null);
    const [loadingCohortSpi, setLoadingCohortSpi] = useState(false);
    const [cohortSpiError, setCohortSpiError] = useState('');

    // 360 Completion Status State
    const [selectedCohortsForStatus, setSelectedCohortsForStatus] = useState([]);
    const [selectedSurveyForStatus, setSelectedSurveyForStatus] = useState('');
    const [surveyCompletionStatus, setSurveyCompletionStatus] = useState(null);
    const [loadingSurveyStatus, setLoadingSurveyStatus] = useState(false);
    const [surveyStatusError, setSurveyStatusError] = useState('');

    // Brand Clarity State
    const [selectedCohortsForBrand, setSelectedCohortsForBrand] = useState([]);

    // Download-related State
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState('');

    const acsChartRef = useRef(null);
    const wepChartRef = useRef(null);

    // Chart constants
    const acsAxisTicks = Array.from({ length: 19 }, (_, i) => i - 9);
    const yAxisDomain = [0, 4];

    // Filter survey rounds based on selected cohorts for the completion status tool
    const filteredSurveyRounds = useMemo(() => {
        if (selectedCohortsForStatus.length === 0) return surveyRounds;
        const selectedIds = new Set(selectedCohortsForStatus.map(id => parseInt(id)));
        return surveyRounds.filter(sr => selectedIds.has(parseInt(sr.cohort_id)));
    }, [selectedCohortsForStatus, surveyRounds]);

    const wepDimensionNames = useMemo(() => config.wepDimensionExplanations ? Object.keys(config.wepDimensionExplanations) : [
        "Context", "Feedback", "Debate", "Hierarchy",
        "Decision-Making", "Relationships", "Conflict", "Chronemics"
    ], []);

    // Memoized data transformation for ACS chart
    const cohortAcsChartData = useMemo(() => {
        if (!cohortSpiSummary || !cohortSpiSummary.members_spi_data) return [];

        const points = {};
        cohortSpiSummary.members_spi_data.forEach(member => {
            if (member.acs_proactivity !== null && member.acs_reactivity !== null) {
                const key = `${member.acs_proactivity.toFixed(2)},${member.acs_reactivity.toFixed(2)}`;
                if (!points[key]) {
                    points[key] = {
                        x: member.acs_proactivity,
                        y: member.acs_reactivity,
                        z: 0,
                        names: [],
                        type: 'member',
                        fill: '#2196f3'
                    };
                }
                points[key].z += 1;
                points[key].names.push(member.name);
            }
        });

        const chartPoints = Object.values(points);

        if (cohortSpiSummary.cohort_average_acs &&
            cohortSpiSummary.cohort_average_acs.proactivity !== null &&
            cohortSpiSummary.cohort_average_acs.reactivity !== null) {
            chartPoints.push({
                x: cohortSpiSummary.cohort_average_acs.proactivity,
                y: cohortSpiSummary.cohort_average_acs.reactivity,
                z: Math.max(...chartPoints.map(p => p.z), 0) + 10 || 20,
                name: `Cohort Average: ${cohortSpiSummary.cohort_average_acs.style_name}`,
                type: 'average',
                fill: '#4caf50'
            });
        }
        return chartPoints;
    }, [cohortSpiSummary]);

    // Memoized data transformation for WEP chart
    const cohortWepScatterData = useMemo(() => {
        if (!cohortSpiSummary || !cohortSpiSummary.members_spi_data || !cohortSpiSummary.cohort_average_wep) return [];

        const data = [];
        wepDimensionNames.forEach((dimName, index) => {
            if (cohortSpiSummary.cohort_average_wep[dimName] !== null && cohortSpiSummary.cohort_average_wep[dimName] !== undefined) {
                data.push({
                    x: index,
                    y: parseFloat(cohortSpiSummary.cohort_average_wep[dimName].toFixed(2)),
                    name: `Average: ${dimName}`,
                    type: 'average',
                    fill: '#e91e63',
                    dimName: dimName
                });
            }
            cohortSpiSummary.members_spi_data.forEach((member, memberIdx) => {
                if (member.wep_scores && member.wep_scores[dimName] !== null && member.wep_scores[dimName] !== undefined) {
                    data.push({
                        x: index,
                        y: parseFloat(member.wep_scores[dimName].toFixed(2)),
                        name: `${member.name}`,
                        type: 'member',
                        fill: memberColors[memberIdx % memberColors.length],
                        dimName: dimName,
                        memberName: member.name,
                        user_id: member.user_id
                    });
                }
            });
        });
        return data;
    }, [cohortSpiSummary, wepDimensionNames]);

    // Fetch initial data (cohorts, survey rounds)
    const fetchInitialData = useCallback(async () => {
        try {
            const [cohortsRes, surveysRes] = await Promise.all([
                api.get(`${config.backendUrl}/cohorts`),
                api.get(`${config.backendUrl}/survey_rounds`)
            ]);
            setCohorts(cohortsRes.data || []);
            setSurveyRounds(surveysRes.data || []);
        } catch (error) {
            console.error("Error fetching initial data for admin tools:", error);
            setError("Failed to load initial tool data (cohorts, surveys).");
        }
    }, []);

    useEffect(() => {
        if (userRole === '1' || userRole === '2') {
            fetchInitialData();
        }
    }, [userRole, fetchInitialData]);

    // Handler to fetch and aggregate SPI summary data for multiple cohorts
    const handleFetchCohortSpiSummary = async () => {
        if (selectedCohortsForSpi.length === 0) {
            setCohortSpiError("Please select at least one cohort.");
            setCohortSpiSummary(null);
            return;
        }
        setLoadingCohortSpi(true);
        setCohortSpiError('');
        setCohortSpiSummary(null);
        try {
            const fetchPromises = selectedCohortsForSpi.map(cohortId =>
                api.get(`${config.backendUrl}/api/cohort/${cohortId}/spi_summary`)
            );
            const results = await Promise.allSettled(fetchPromises);
            const successfulResults = results
                .filter(res => res.status === 'fulfilled' && res.value.data && res.value.data.user_count_with_spi_data > 0)
                .map(res => res.value.data);

            if (successfulResults.length === 0) {
                throw new Error("No SPI data found for the selected cohort(s). Users may need to complete it first.");
            }

            // Aggregate data from all selected cohorts
            const allMembers = successfulResults.flatMap(r => r.members_spi_data);
            const aggregatedSummary = {
                cohort_name: successfulResults.map(r => r.cohort_name).join(', '),
                user_count_with_spi_data: allMembers.length,
                members_spi_data: allMembers,
                cohort_average_acs: {},
                cohort_average_wep: {},
                message: `Aggregated data from ${successfulResults.length} cohort(s).`
            };

            // Recalculate ACS averages
            const allMembersWithAcs = allMembers.filter(m => m.acs_proactivity !== null && m.acs_reactivity !== null);
            const totalAcsPro = allMembersWithAcs.reduce((acc, m) => acc + (m.acs_proactivity || 0), 0);
            const totalAcsReact = allMembersWithAcs.reduce((acc, m) => acc + (m.acs_reactivity || 0), 0);
            aggregatedSummary.cohort_average_acs.proactivity = allMembersWithAcs.length > 0 ? totalAcsPro / allMembersWithAcs.length : null;
            aggregatedSummary.cohort_average_acs.reactivity = allMembersWithAcs.length > 0 ? totalAcsReact / allMembersWithAcs.length : null;
            aggregatedSummary.cohort_average_acs.style_name = getAcsStyleName(aggregatedSummary.cohort_average_acs.proactivity, aggregatedSummary.cohort_average_acs.reactivity);

            // Recalculate WEP averages
            const wepAggregates = new Map();
            allMembers.forEach(member => {
                Object.entries(member.wep_scores || {}).forEach(([dim, score]) => {
                    if (score !== null) {
                        if (!wepAggregates.has(dim)) {
                            wepAggregates.set(dim, { sum: 0, count: 0 });
                        }
                        const current = wepAggregates.get(dim);
                        current.sum += score;
                        current.count += 1;
                    }
                });
            });
            aggregatedSummary.cohort_average_wep = Object.fromEntries(
                Array.from(wepAggregates.entries()).map(([dim, data]) => [
                    dim,
                    data.count > 0 ? data.sum / data.count : null,
                ])
            );
            setCohortSpiSummary(aggregatedSummary);
        } catch (error) {
            console.error("Error fetching cohort SPI summary:", error);
            setCohortSpiError(error.response?.data?.message || error.message || "Failed to load cohort SPI summary.");
            setCohortSpiSummary(null);
        } finally {
            setLoadingCohortSpi(false);
        }
    };

    // Handler to fetch and aggregate 360 completion status
    const handleFetchSurveyCompletionStatus = async () => {
        if (selectedCohortsForStatus.length === 0 || !selectedSurveyForStatus) {
            setSurveyStatusError("Please select a survey and at least one cohort.");
            setSurveyCompletionStatus(null);
            return;
        }
        setLoadingSurveyStatus(true);
        setSurveyStatusError('');
        setSurveyCompletionStatus(null);
        try {
            const fetchPromises = selectedCohortsForStatus.map(cohortId =>
                api.get(`${config.backendUrl}/api/survey_round/${selectedSurveyForStatus}/completion_status?cohort_id=${cohortId}`)
            );
            const results = await Promise.allSettled(fetchPromises);
            const successfulResults = results
                .filter(res => res.status === 'fulfilled' && res.value.data)
                .map(res => res.value.data);

            if (successfulResults.length === 0) {
                throw new Error("Could not fetch completion status for the selected options.");
            }

            // Aggregate non-completers from all selected cohorts, removing duplicates
            const allNonCompleters = successfulResults.flatMap(r => r.non_completers || []);
            const uniqueNonCompleters = Array.from(new Map(allNonCompleters.map(user => [user.user_id, user])).values())
                .sort((a, b) => a.name.localeCompare(b.name));

            const totalUsersChecked = successfulResults.reduce((acc, curr) => acc + (curr.total_users_in_cohort_checked || 0), 0);

            setSurveyCompletionStatus({
                non_completers: uniqueNonCompleters,
                total_users_in_cohort_checked: totalUsersChecked
            });

        } catch (error) {
            console.error("Error fetching survey completion status:", error);
            setSurveyStatusError(error.response?.data?.message || error.message || "Failed to load survey completion status.");
            setSurveyCompletionStatus(null);
        } finally {
            setLoadingSurveyStatus(false);
        }
    };

    // Generic download handler for reports that take filters
    const handleDownload = useCallback(async (url, filenamePrefix, selectedCohortIds) => {
        if (!selectedCohortIds || selectedCohortIds.length === 0) {
            setDownloadError("Please select at least one cohort to start a download.");
            return;
        }
        setDownloadError('');
        setIsDownloading(true);
        try {
            const params = new URLSearchParams();
            selectedCohortIds.forEach(id => params.append('cohort_id', id));

            const fullUrl = `${config.backendUrl}${url}?${params.toString()}`;
            const response = await api.get(fullUrl, { responseType: 'blob' });

            const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
            const link = document.createElement('a');
            link.href = blobUrl;
            const timestamp = new Date().toISOString().slice(0, 19).replace(/[-T:]/g, "");
            link.setAttribute('download', `${filenamePrefix}_${timestamp}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error(`Error downloading ${filenamePrefix}:`, err);
            let message = `An error occurred while downloading the ${filenamePrefix} report.`;
            if (err.response && err.response.data && err.response.data instanceof Blob) {
                try {
                    const errorText = await err.response.data.text();
                    const errorJson = JSON.parse(errorText);
                    message = errorJson.message || errorJson.error || message;
                } catch (parseError) { console.error("Could not parse error response:", parseError); }
            } else if (err.response?.data?.message) { message = err.response.data.message; }
            setDownloadError(message);
        } finally {
            setIsDownloading(false);
        }
    }, []);

    // Specific download functions
    const downloadQualitativeCsv = () => handleDownload('/reports/download/qualitative', 'qualitative_feedback', selectedCohortsForStatus);
    const downloadAllCsv = () => handleDownload('/reports/download', 'all_360_data', selectedCohortsForStatus);
    const downloadSpiCsv = () => handleDownload(`/api/admin/spi/responses/download`, 'spi_responses', selectedCohortsForSpi);
    const downloadCharismaCsv = () => handleDownload(`/api/admin/charisma/responses/download`, 'charisma_responses', selectedCohortsForSpi);
    const downloadBrandCsv = () => handleDownload('/api/admin/brand/download', 'brand_clarity', selectedCohortsForBrand);

    const handleDownloadChart = async (chartRef, filenamePrefix) => {
        if (chartRef.current) {
            try {
                const canvas = await html2canvas(chartRef.current, {
                    logging: true,
                    useCORS: true,
                    backgroundColor: '#ffffff',
                    scale: 2
                });
                const image = canvas.toDataURL('image/png', 1.0);
                const link = document.createElement('a');
                link.download = `${filenamePrefix}_${new Date().toISOString().slice(0, 10)}.png`;
                link.href = image;
                link.click();
            } catch (error) {
                console.error('Error downloading chart:', error);
                const errorSetter = chartRef === acsChartRef ? setCohortSpiError : setSurveyStatusError;
                errorSetter(`Failed to download chart.`);
            }
        }
    };
    
    // De-select a specific cohort from a state array
    const handleCohortDelete = (cohortIdToDelete, stateSetter) => {
        stateSetter(prev => prev.filter(id => id !== cohortIdToDelete));
    };


    if (!(userRole === '1' || userRole === '2')) {
        return null;
    }

    // Common props for cohort multi-select
    const cohortSelectProps = (value, onChange) => ({
        multiple: true,
        value: value,
        onChange: (event) => {
            const { target: { value } } = event;
            onChange(typeof value === 'string' ? value.split(',') : value);
        },
        input: <OutlinedInput label="Select Cohort(s)" />,
        renderValue: (selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((id) => {
                    const cohort = cohorts.find(c => c.cohort_id === id);
                    return <Chip 
                                key={id} 
                                label={cohort ? cohort.cohort_name : id} 
                                size="small"
                                onDelete={() => handleCohortDelete(id, onChange)}
                                onMouseDown={(event) => event.stopPropagation()} // Prevent menu from opening on delete click
                           />;
                })}
            </Box>
        ),
    });

    return (
        <Accordion
            expanded={isAccordionExpanded}
            onChange={() => setIsAccordionExpanded(!isAccordionExpanded)}
            sx={{ width: '100%', mt: 3, boxShadow: 3 }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="admin-tools-content" id="admin-tools-header">
                <Typography variant="h6">Admin & Moderator Tools</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: { xs: 1, md: 2 } }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {downloadError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDownloadError('')}>{downloadError}</Alert>}

                <Grid container spacing={2}>
                    {/* --- LEFT COLUMN --- */}
                    <Grid item xs={12} lg={6} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* SPI Analysis Card */}
                        <Paper elevation={2} sx={{ p: 2, width: '100%', display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
                            <Typography variant="h6" gutterBottom>Cohort SPI Analysis</Typography>
                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                <InputLabel id="cohort-spi-select-label">Select Cohort(s)</InputLabel>
                                <Select labelId="cohort-spi-select-label" {...cohortSelectProps(selectedCohortsForSpi, setSelectedCohortsForSpi)}>
                                    {cohorts.map(c => <MenuItem key={c.cohort_id} value={c.cohort_id}>{c.cohort_name}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <Button variant="contained" onClick={handleFetchCohortSpiSummary} disabled={loadingCohortSpi || selectedCohortsForSpi.length === 0} sx={{ mb: 2 }}>
                                {loadingCohortSpi ? <CircularProgress size={24} /> : "Load SPI Summary"}
                            </Button>
                            {cohortSpiError && <Alert severity="error" sx={{ mb: 2 }}>{cohortSpiError}</Alert>}
                            
                            {cohortSpiSummary && (
                                <Box>
                                    {cohortSpiSummary.message && (
                                        <Alert severity={cohortSpiSummary.user_count_with_spi_data < 3 ? "warning" : "info"} sx={{ mb: 2 }}>
                                            {cohortSpiSummary.message}
                                        </Alert>
                                    )}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                                        {/* ACS Chart */}
                                        <Box>
                                            <Typography variant="subtitle1" gutterBottom>Attitudinal Communication Styles</Typography>
                                            <Typography variant="body2" sx={{mb: 1}}>Aggregate Average: {cohortSpiSummary.cohort_average_acs.style_name}</Typography>
                                            <Box ref={acsChartRef} sx={{ width: '100%', aspectRatio: '1 / 1', border: '1px solid lightgrey', p: 1, boxSizing: 'border-box' }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 0 }}>
                                                        <CartesianGrid />
                                                        <XAxis type="number" dataKey="x" name="Proactivity" domain={[-9, 9]} ticks={acsAxisTicks} />
                                                        <YAxis type="number" dataKey="y" name="Reactivity" domain={[-9, 9]} ticks={acsAxisTicks} />
                                                        <ZAxis type="number" dataKey="z" range={[60, 500]} name="count" />
                                                        <Tooltip cursor={{ strokeDasharray: '3 3' }} content={({ active, payload }) => active && payload?.length && (<Paper sx={{ p: 1 }}><Typography variant="caption">{payload[0].payload.type === 'average' ? payload[0].payload.name : `${payload[0].payload.names.join(', ')} (${payload[0].payload.z} user(s))`}</Typography></Paper>)} />
                                                        <Legend verticalAlign="top" height={36} payload={[{ value: 'Individuals', type: 'circle', id: 'ind', color: '#2196f3' }, { value: 'Average', type: 'star', id: 'avg', color: '#4caf50' }]}/>
                                                        <ReferenceLine x={0} stroke="#757575" strokeDasharray="5 5" /><ReferenceLine y={0} stroke="#757575" strokeDasharray="5 5" />
                                                        <ReferenceArea x1={0.05} x2={9} y1={0.05} y2={9} stroke="none" fill="#e0f7fa" fillOpacity={0.2} label={{ value: "Expressive", position: "insideTopRight", fill:"#00796b", fontSize: "0.8rem", dy: -5, dx: -5 }}/>
                                                        <ReferenceArea x1={-9} x2={-0.05} y1={-9} y2={-0.05} stroke="none" fill="#fff3e0" fillOpacity={0.2} label={{ value: "Analytical", position: "insideBottomLeft", fill:"#ef6c00", fontSize: "0.8rem", dy: 5, dx: 5}}/>
                                                        <ReferenceArea x1={0.05} x2={9} y1={-9} y2={-0.05} stroke="none" fill="#e8f5e9" fillOpacity={0.2} label={{ value: "Driver", position: "insideBottomRight", fill:"#2e7d32", fontSize: "0.8rem", dy: 5, dx: -5}}/>
                                                        <ReferenceArea x1={-9} x2={-0.05} y1={0.05} y2={9} stroke="none" fill="#fce4ec" fillOpacity={0.2} label={{ value: "Amiable", position: "insideTopLeft", fill:"#d81b60", fontSize: "0.8rem", dy: -5, dx: 5}}/>
                                                        <Scatter name="Individuals" data={cohortAcsChartData.filter(p => p.type === 'member')} shape={<CustomCohortACSShape type="member" />} />
                                                        <Scatter name="Average" data={cohortAcsChartData.filter(p => p.type === 'average')} shape={<CustomCohortACSShape type="average" />} fill="#4caf50" />
                                                    </ScatterChart>
                                                </ResponsiveContainer>
                                            </Box>
                                            <Button onClick={() => handleDownloadChart(acsChartRef, `cohort_acs_summary`)} size="small" startIcon={<DownloadIcon />}>Download ACS Chart</Button>
                                        </Box>
                                        {/* WEP Chart */}
                                        <Box>
                                            <Typography variant="subtitle1" gutterBottom>Work Environment Preferences (Averages)</Typography>
                                            <Box ref={wepChartRef} sx={{ width: '100%', aspectRatio: '2 / 1', mt: 1, border: '1px solid lightgrey', p: 1, boxSizing: 'border-box' }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <ScatterChart margin={{ top: 20, right: 20, bottom: 60, left: 0 }}>
                                                        <CartesianGrid horizontal={false} />
                                                        <XAxis type="number" dataKey="x" domain={[-0.5, wepDimensionNames.length - 0.5]} ticks={wepDimensionNames.map((_, i) => i)} tickFormatter={(tick) => wepDimensionNames[tick] || ''} angle={-45} textAnchor="end" height={70} interval={0} style={{ fontSize: '0.7rem' }} />
                                                        <YAxis type="number" dataKey="y" name="Score" domain={yAxisDomain} ticks={[0, 1, 2, 3, 4]} />
                                                        <Tooltip cursor={{ strokeDasharray: '3 3' }} content={({ active, payload }) => active && payload?.length && (<Paper elevation={3} sx={{ p: 1 }}><Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>{payload[0].payload.type === 'average' ? `Average: ${payload[0].payload.dimName}` : `${payload[0].payload.memberName}: ${payload[0].payload.dimName}`}</Typography><Typography variant="caption" display="block">{`Score: ${payload[0].payload.y.toFixed(2)}`}</Typography></Paper>)} />
                                                        <Legend verticalAlign="top" height={36} payload={[{ value: 'Individuals', type: 'circle', id: 'ind_wep', color: memberColors[0] }, { value: 'Average', type: 'diamond', id: 'avg_wep', color: '#e91e63' }]} />
                                                        <ReferenceArea y1={0} y2={1.33} fill={lowScoreBg} fillOpacity={0.4} label={{ value: "Low Range", position: "insideTopLeft", fill: "#555", fontSize: "0.7rem", dy: 5, dx: 5 }} />
                                                        <ReferenceArea y1={1.33} y2={2.67} fill={midScoreBg} fillOpacity={0.4} label={{ value: "Mid Range", position: "insideTopLeft", fill: "#555", fontSize: "0.7rem", dy: 5, dx: 5 }} />
                                                        <ReferenceArea y1={2.67} y2={4} fill={highScoreBg} fillOpacity={0.4} label={{ value: "High Range", position: "insideTopLeft", fill: "#555", fontSize: "0.7rem", dy: 5, dx: 5 }} />
                                                        <Scatter name="Individuals" data={cohortWepScatterData.filter(d => d.type === 'member' && d.y !== null)} shape={<CustomCohortWEPShape type="member" />} />
                                                        <Scatter name="Average" data={cohortWepScatterData.filter(d => d.type === 'average' && d.y !== null)} shape={<CustomCohortWEPShape type="average" />} fill="#e91e63" />
                                                    </ScatterChart>
                                                </ResponsiveContainer>
                                            </Box>
                                            <Button onClick={() => handleDownloadChart(wepChartRef, `cohort_wep_summary`)} size="small" startIcon={<DownloadIcon />}>Download WEP Chart</Button>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                             <Divider sx={{ my: 2 }} />
                             <Typography variant="subtitle2" sx={{mb:1}}>SPI/Charisma Report Downloads</Typography>
                            <Box sx={{display: 'flex', gap: 1}}>
                                <Button size="small" variant="outlined" onClick={downloadSpiCsv} disabled={isDownloading || selectedCohortsForSpi.length === 0} startIcon={<DownloadIcon/>}>SPI CSV</Button>
                                <Button size="small" variant="outlined" onClick={downloadCharismaCsv} disabled={isDownloading || selectedCohortsForSpi.length === 0} startIcon={<DownloadIcon/>}>Charisma CSV</Button>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* --- RIGHT COLUMN --- */}
                    <Grid item xs={12} lg={6} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* 360 Completion Card */}
                         <Paper elevation={2} sx={{ p: 2, width: '100%', display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
                            <Typography variant="h6" gutterBottom>360° Survey Completion</Typography>
                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                <InputLabel id="cohort-status-select-label">Select Cohort(s)</InputLabel>
                                <Select labelId="cohort-status-select-label" {...cohortSelectProps(selectedCohortsForStatus, setSelectedCohortsForStatus)}>
                                    {cohorts.map(c => <MenuItem key={c.cohort_id} value={c.cohort_id}>{c.cohort_name}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={selectedCohortsForStatus.length === 0}>
                                <InputLabel id="survey-status-select-label">Select Survey Round</InputLabel>
                                <Select
                                    labelId="survey-status-select-label"
                                    value={selectedSurveyForStatus}
                                    label="Select Survey Round"
                                    onChange={(e) => { setSelectedSurveyForStatus(e.target.value); setSurveyCompletionStatus(null); setSurveyStatusError(''); }}
                                >
                                    <MenuItem value=""><em>Select a Survey</em></MenuItem>
                                    {filteredSurveyRounds.map(sr => <MenuItem key={sr.survey_id} value={sr.survey_id}>{sr.survey_name} (Ends: {new Date(sr.end_date).toLocaleDateString()})</MenuItem>)}
                                </Select>
                            </FormControl>
                            <Button variant="contained" onClick={handleFetchSurveyCompletionStatus} disabled={loadingSurveyStatus || !selectedSurveyForStatus || selectedCohortsForStatus.length === 0} sx={{ mb: 2 }}>
                                {loadingSurveyStatus ? <CircularProgress size={24} /> : "View Non-Completion Status"}
                            </Button>
                            {surveyStatusError && <Alert severity="error" sx={{ mb: 2 }}>{surveyStatusError}</Alert>}
                            {surveyCompletionStatus && (
                                <Box sx={{ flexGrow: 1, maxHeight: 200, overflowY: 'auto' }}>
                                    <Typography variant="subtitle1">Non-Completers for "{surveyRounds.find(sr => sr.survey_id === parseInt(selectedSurveyForStatus))?.survey_name}":</Typography>
                                    {surveyCompletionStatus.non_completers && surveyCompletionStatus.non_completers.length > 0 ? (
                                        <List dense disablePadding>
                                            {surveyCompletionStatus.non_completers.map(user => (
                                                <ListItem key={user.user_id} disablePadding>
                                                    <ListItemText primary={`${user.name} (${user.email})`} secondary={`Team: ${user.team_name || 'N/A'}`} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    ) : (
                                        <Typography color="text.secondary" sx={{ mt: 1 }}>All eligible users have completed this survey round.</Typography>
                                    )}
                                </Box>
                            )}
                             <Divider sx={{ my: 2 }} />
                             <Typography variant="subtitle2" sx={{mb:1}}>360 Report Downloads</Typography>
                             <Box sx={{display: 'flex', gap: 1}}>
                                <Button size="small" variant="outlined" onClick={downloadQualitativeCsv} disabled={isDownloading || selectedCohortsForStatus.length === 0} startIcon={<DownloadIcon/>}>Qualitative CSV</Button>
                                <Button size="small" variant="outlined" onClick={downloadAllCsv} disabled={isDownloading || selectedCohortsForStatus.length === 0} startIcon={<DownloadIcon/>}>All Data CSV</Button>
                             </Box>
                        </Paper>

                        {/* Brand Clarity Card */}
                        <Paper elevation={2} sx={{ p: 2, width: '100%', display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
                            <Typography variant="h6" gutterBottom>Brand Clarity Report</Typography>
                             <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                <InputLabel id="cohort-brand-select-label">Select Cohort(s)</InputLabel>
                                <Select labelId="cohort-brand-select-label" {...cohortSelectProps(selectedCohortsForBrand, setSelectedCohortsForBrand)}>
                                    {cohorts.map(c => <MenuItem key={c.cohort_id} value={c.cohort_id}>{c.cohort_name}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <Button
                                variant="contained"
                                onClick={downloadBrandCsv}
                                disabled={isDownloading || selectedCohortsForBrand.length === 0}
                                startIcon={isDownloading ? <CircularProgress size={20} /> : <DownloadIcon />}
                            >
                                Download Audience/Goal Data (CSV)
                            </Button>
                        </Paper>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}

export default AdminTools;
